/**
 * Zod input schemas: contentManager. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
// ── Content Manager (ADM) — sourcing prototype ─────────────────────────────
export const ContentSourceStartSchema = z.object({
    url: z.string().trim().url().max(500),
    clientName: z.string().trim().max(200).optional(),
    /** One or MORE comma-separated paths, e.g. '/en/benches, /en/tables' (drives
     *  crawl priority, product classification and the dynamic cap). Omitted =
     *  "no custom product URL": crawl the main URL, classify heuristically,
     *  general 250-page cap only (no product-path extension). */
    productUrlHint: z.string().trim().min(2).max(600).optional(),
    resultsLanguage: z.enum(['original', 'en', 'en-es']).optional(),
    /** With an English results language: translate PRODUCT NAMES too (brand and
     *  trademarked line names stay as branded). Off = names stay as on the site. */
    translateNames: z.boolean().optional(),
    clientMode: z.enum(['new', 'update']).nullish(),
    /** Update clients: link the ArchDaily catalog right at start (market +
     *  company id from the SFDC lookup, or a pasted company URL). */
    archdaily: z.object({
        market: z.enum(['ADEN', 'ADBR', 'ADCL', 'ADMX']),
        companyId: z.string().trim().regex(/^\d{1,8}$/),
        accountName: z.string().trim().max(200).nullish(),
    }).nullish(),
});
export const ContentSourceDeleteSchema = z.object({ sourceId: z.string().min(1) });
export const ContentSourceStopSchema = z.object({ sourceId: z.string().min(1) });
export const ContentProposalGenerateSchema = z.object({
    sourceId: z.string().min(1),
    /** The client's package size — how many pages the proposal may have. */
    pageCount: z.number().int().min(1).max(60).nullish().transform((v) => v ?? 20),
    /** Which page kinds to propose. */
    pageTypes: z.enum(['both', 'product', 'application']).nullish().transform((v) => v ?? 'both'),
});
/** Set/clear a source's client mode (new vs update) after the crawl. */
export const ContentSourceClientModeSchema = z.object({
    sourceId: z.string().min(1),
    clientMode: z.enum(['new', 'update']).nullable(),
});
/** Search the SFDC AD-clients report by account name. */
export const ContentArchdailyLookupSchema = z.object({
    query: z.string().trim().min(2).max(120),
});
/** Fetch the existing ArchDaily catalog for a source: by market + company id,
 *  or by a pasted company URL (…/catalog/<lang>/companies/<id>/…). */
export const ContentArchdailySyncSchema = z.object({
    sourceId: z.string().min(1),
    market: z.enum(['ADEN', 'ADBR', 'ADCL', 'ADMX']).nullish(),
    companyId: z.string().trim().regex(/^\d{1,8}$/).nullish(),
    companyUrl: z.string().trim().url().max(500).nullish(),
    accountName: z.string().trim().max(200).nullish(),
});
export const ContentProposalSaveSchema = z.object({
    sourceId: z.string().min(1),
    pages: z.array(z.object({
        // Empty allowed: the board autosaves a page while its title is still being typed.
        title: z.string().trim().max(200),
        scope: z.enum(['single-product', 'product-group', 'collection', 'family', 'other']),
        pageType: z.enum(['product', 'application', 'guide']).nullish(),
        status: z.enum(['new', 'online']).nullish(),
        sourceUrl: z.string().trim().max(600).nullish(),
        archdailyUrl: z.string().trim().max(600).nullish(),
        itemIds: z.array(z.string().min(1)).max(100),
        itemNames: z.array(z.string().max(200)).max(100),
        rationale: z.string().trim().max(1000).nullish(),
    })).max(120),
});
export const ContentProposalExportSchema = z.object({ sourceId: z.string().min(1) });
/** Manual re-categorization: move item(s) to a family/collection (undefined =
 *  leave that field alone, null = clear it). */
export const ContentItemRegroupSchema = z.object({
    sourceId: z.string().min(1),
    itemIds: z.array(z.string().min(1)).min(1).max(200),
    family: z.string().trim().max(160).nullable().optional(),
    collection: z.string().trim().max(160).nullable().optional(),
});
/** Update clients: compare the client's ONLINE ArchDaily products (from the
 *  analytics catalogue mirror of Neufert) with the crawled website items. */
export const ContentCatalogInfoCheckSchema = z.object({ sourceId: z.string().min(1) });
export const ContentSourceContinueSchema = z.object({
    sourceId: z.string().min(1),
    /** Continue into NEWLY defined product path(s) (comma/line-separated; Laura,
     *  23 Sep): they are crawled first, classified as products and added to the
     *  source's product paths. Works on any finished crawl, also a complete one. */
    newPaths: z.string().trim().min(2).max(600).optional(),
});
//# sourceMappingURL=contentManager.js.map